<template>
  <v-container fluid>
    <v-row v-if="isAdmin">
      <v-col cols="6">
        <v-text-field
          :placeholder="$t('rightsholders.searchPlaceholder')"
          v-model="searchQuery"
        />
      </v-col>
      <v-spacer/>
      <v-col cols="auto">
        <v-btn color="primary" @click="sendNotificationDialog = true">{{
            $t('rightsholders.sendNotification.button')
          }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      class="no-select-table"
      :loading="!$store.getters['app/rightsholders'].list || portionLoading"
      :loading-text="$t('rightsholders.loadingItems')"
      :items="visibleRightsholders"
      :items-per-page.sync="itemsPerPage"
      :headers="headers"
      :show-select="isAdmin"
      :page.sync="currentPage"
      v-model="selected"
      item-key="rowNumber"
      :expanded.sync="expandedRows"
      @click:row="openDetails($event)"
      :footer-props="{itemsPerPageOptions: [25, 50], pageText: continuosAmount ? `{0} / ${visibleRightsholders.length -  1}+` : '$vuetify.dataFooter.pageText'}"
    >
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" v-if="item">
          <v-row>
            <v-col lg="3" md="6" sm="12">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <div class="text-caption">{{ $t('rightsholders.fullAddress') }}</div>
                    <div class="text-body-2" ref="fullAddressContainer">
                      <p v-if="item.cnamn.trim()" class="ma-0">{{ item.cnamn }}</p>
                      <p v-if="item.address1.trim()"  class="ma-0">{{ item.address1 }}</p>
                      <p v-if="item.address2.trim()" class="ma-0">{{ item.address2 }}</p>
                      <p v-if="item.address3.trim()" class="ma-0">{{ item.address3 }}</p>
                      <p v-if="item.padr.trim()" class="ma-0">{{ item.padr }}</p>
                      <p v-if="item.land.trim()" class="ma-0">{{ item.land }}</p>
                    </div>
                    <div class="text-body-2">
                      <v-btn small outlined color="primary" @click="copyAddress">{{ $t('rightsholders.copy.button') }}</v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col lg="3" md="6" sm="12" v-for="(extras, i) in [item.extra, item.extra2]" :key="i">
              <v-list dense>
                <v-list-item v-for="extra in extras" :key="extra.text[$i18n.locale]">
                  <v-list-item-content>
                    <div class="text-caption">{{ extra.label && extra.label[$i18n.locale] }}</div>
                    <div class="text-body-2">{{ extra.text && extra.text[$i18n.locale] }}</div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-row justify="end">
            <v-col cols="auto">
              <v-btn text color="primary" @click="openDetails(item)">
                {{ $t('rightsholders.hide') }}
              </v-btn>
            </v-col>
          </v-row>
        </td>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{on, attrs}">
            <v-btn icon v-on="on" v-bind="attrs">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="openDetails(item)">
              <v-list-item-title>{{ $t('rightsholders.menu.details') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setAsCurrent(item)">
              <v-list-item-title>{{ $t('rightsholders.menu.setCurrent') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <v-dialog v-model="sendNotificationDialog" max-width="50%">

      <DialogTemplate
        :title="$t('rightsholders.sendNotification.title')"
        @close="sendNotificationDialog = false"
        @send="onSendNotification"
        v-if="sendNotificationDialog"
      >
        <v-container>
          <v-row no-gutters>
            <v-col cols="12">
              <p class="text-caption">{{ $t('rightsholders.sendNotification.to') }}</p>
              <p class="text-body-2">{{ notificationTargetsText }}</p>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                :label="$t('notifications.dialog.title')"
                class="t-admin-compose-title"
                v-model="newNotificationTitle"
                :rules="[value => value.trim().length > 0 || $t('rightsholders.sendNotification.rules.emptyHead')]"
              ></v-text-field>
            </v-col>
          </v-row>

        </v-container>
      </DialogTemplate>
    </v-dialog>
  </v-container>
</template>

<script>
import DialogTemplate from "@/components/common/notifications/DialogTemplate";
import {mapGetters} from "vuex";
import Rightsholder from "@/models/Rightsholder";
import copy from 'copy-to-clipboard';

export default {
  components: {DialogTemplate},
  data() {
    return {
      pages: 1,
      itemsPerPage: 50,
      currentPage: 1,
      sendNotificationDialog: false,
      selected: [],
      expandedRows: [],
      newNotificationTitle: String(),
      portionLoading: false,
      searchQuery: '',
    }
  },

  methods: {
    openDetails(item) {
      this.expandedRows = this.expandedRows.includes(item) ?
        this.expandedRows.filter((x) => x !== item) :
        this.expandedRows.concat([item]);
    },

    setAsCurrent(item) {
      this.portionLoading = true;
      this.$store.dispatch('app/resetCurrentRightsholder', item.id)
        .then(() => {
          this.portionLoading = false;
        });
    },

    onSendNotification({text}) {

      if (!this.newNotificationTitle) {
        return this.$toast(this.$t('rightsholders.sendNotification.rules.emptyHead'));
      }

      if (!text) {
        return this.$toast(this.$t('rightsholders.sendNotification.rules.emptyText'));
      }

      // 0 or one number or Array<number>
      const to = this.selected.length === 0 ? 0 : (this.selected.length === 1 ? [this.selected[0].id] : this.selected.map(({id}) => id));

      this.$store.dispatch('app/sendNotificationFromAdmin', {text, title: this.newNotificationTitle, to})
        .then(() => {
          this.$toast(this.$t('rightsholders.sendNotification.messageSent', {target: this.notificationTargetsText}));
          this.sendNotificationDialog = false;
          this.newNotificationTitle = '';
        });
    },

    loadRightsholders() {
      this.$store.dispatch('app/loadRightsholders')
    },

    copyAddress() {
      copy(this.$refs.fullAddressContainer.innerText.replace(/\n\n/g, '\n') , {
        format: 'text/plain'
      });
      this.$toast(this.$t('rightsholders.copy.done'));
    }
  },

  watch: {
    currentPage(v) {
      if (v === (this.lastPage + 1) && this.continuosAmount) {
        this.loadRightsholders();
      }
    }
  },

  computed: {

    ...mapGetters('app', ['rightsholders']),

    isAdmin() {
      return this.$store.getters["app/isAdmin"];
    },

    notificationTargetsText() {
      const hidePart = (items) => items.slice(0, 3)
        .map((x) => x.name)
        .join(', ') + ' and ' + (items.length - 3) + ' more';

      if (this.selected.length === 0) {
        // return hidePart(this.visibleRightsholders);
        return this.$t('rightsholders.sendNotification.targetAll');
      }
      if (this.selected.length < 4) {
        return this.selected.map((x) => x.name)
          .join(', ')
      }
      return hidePart(this.selected);

    },

    detailsScheme() {
      const t = (columnName) => this.$t(`rightsholders.columns.${columnName}`);
      const getCol = (col) => ({key: col, title: t(col)});

      return [[
        getCol('country'),
        getCol('tel'),
      ]]
    },

    headers() {
      const getHeader = (key) => ({value: key, text: this.$t(`rightsholders.columns.${key}`)});
      return ['bol', 'name', 'tel', 'orgNumber', 'VATNumber']
        .map(getHeader)
        .concat([{
          value: 'actions',
          sortable: false,
        }]);
    },

    visibleRightsholders() {
      const contains = (haystack, needle) => String(haystack)
        .toLowerCase()
        .includes(needle.toLowerCase()
          .trim());
      const filter = (list) => list.filter((x) => x instanceof Rightsholder)
        .filter(({bol, cnamn, orgnr}) =>
          contains(bol, this.searchQuery) ||
          contains(cnamn, this.searchQuery) ||
          contains(orgnr, this.searchQuery)
        );
      return filter((this.rightsholders.list || []).concat(this.rightsholders.finished ? [] : [null]));
    },

    lastPage() {
      return Math.ceil((this.visibleRightsholders.length - Number(this.continuosAmount)) / this.itemsPerPage);
    },

    continuosAmount() {
      return !this.rightsholders.finished;
    }
  }
}
</script>